import type { Database } from 'sql.js';
import { queryOne } from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import type { LocalSqliteRuntime } from '@src/modules/shared/local-sqlite/domain/LocalSqliteRuntime.ts';
import { readBrowserStorageEstimate } from '@src/modules/shared/local-sqlite/domain/browserStorageEstimate.ts';
import type { SyncStorageMetricsPort, SyncStorageSnapshot } from '../../domain/ports/SyncStorageMetricsPort.ts';

export { readBrowserStorageEstimate };

function scalarTotal(db: Database, sql: string): number {
  const row = queryOne(db, sql, []);
  if (!row) return 0;
  const raw = row.total;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function imagePayloadBytes(db: Database): number {
  const q = (sql: string) => scalarTotal(db, sql);
  return (
    q(
      `SELECT COALESCE(SUM(CASE WHEN informe_pdf LIKE 'data:image%' THEN LENGTH(informe_pdf) ELSE 0 END),0) AS total FROM punto_entrega`,
    ) +
    q(`SELECT COALESCE(SUM(
      CASE WHEN acta LIKE 'data:image%' THEN LENGTH(acta) ELSE 0 END +
      CASE WHEN firma_base64 LIKE 'data:image%' THEN LENGTH(firma_base64) ELSE 0 END +
      CASE WHEN registro_fotografico LIKE 'data:image%' THEN LENGTH(registro_fotografico) ELSE 0 END
    ),0) AS total FROM punto_entrega_participante`) +
    q(`SELECT COALESCE(SUM(
      CASE WHEN acta LIKE 'data:image%' THEN LENGTH(acta) ELSE 0 END +
      CASE WHEN firma_base64 LIKE 'data:image%' THEN LENGTH(firma_base64) ELSE 0 END +
      CASE WHEN registro_fotografico LIKE 'data:image%' THEN LENGTH(registro_fotografico) ELSE 0 END
    ),0) AS total FROM redencion_bono_participante`) +
    q(`SELECT COALESCE(SUM(
      CASE WHEN documento_beneficiario_frontal_path LIKE 'data:image%' THEN LENGTH(documento_beneficiario_frontal_path) ELSE 0 END +
      CASE WHEN documento_beneficiario_reverso_path LIKE 'data:image%' THEN LENGTH(documento_beneficiario_reverso_path) ELSE 0 END +
      CASE WHEN documento_autorizado_frontal_path LIKE 'data:image%' THEN LENGTH(documento_autorizado_frontal_path) ELSE 0 END +
      CASE WHEN documento_autorizado_reverso_path LIKE 'data:image%' THEN LENGTH(documento_autorizado_reverso_path) ELSE 0 END +
      CASE WHEN autorizado_path LIKE 'data:image%' THEN LENGTH(autorizado_path) ELSE 0 END
    ),0) AS total FROM acudientes`)
  );
}

export function createSqlJsSyncStorageMetricsPort(
  runtime: LocalSqliteRuntime | undefined,
): SyncStorageMetricsPort {
  return {
    async getSnapshot(): Promise<SyncStorageSnapshot> {
      const o = await readBrowserStorageEstimate();
      const db = runtime?.getDb();
      let databaseFileBytes = 0;
      let embeddedImagePayloadBytes = 0;
      if (db) {
        try {
          databaseFileBytes = new Uint8Array(db.export()).byteLength;
          embeddedImagePayloadBytes = imagePayloadBytes(db);
        } catch {
          /* ignore */
        }
      }
      return {
        originQuotaBytes: o?.quotaBytes ?? null,
        originUsageBytes: o?.usageBytes ?? null,
        databaseFileBytes,
        embeddedImagePayloadBytes,
      };
    },
  };
}
